/**
 * x-rich-text-editor: Quill 初期化と hidden への HTML 同期
 * 依存: window.Quill（resources/js/env/quill.js）
 */

const richTextEditorMap = new WeakMap();

function isRichTextEditorExternallyUpdatable(rootEl) {
    return rootEl?.dataset.richTextExternallyUpdatable === '1';
}

function registerRichTextEditor(rootEl, quill) {
    richTextEditorMap.set(rootEl, quill);
}

function getInitialHtml(rootEl) {
    const payload = rootEl.querySelector('[data-rich-text-payload]');
    if (!payload || !payload.textContent) {
        return '';
    }
    try {
        const data = JSON.parse(payload.textContent.trim());
        payload.remove();
        return typeof data.html === 'string' ? data.html : '';
    } catch {
        payload.remove();
        return '';
    }
}

function syncToHidden(quill, hiddenInput) {
    if (typeof quill.getSemanticHTML === 'function') {
        hiddenInput.value = quill.getSemanticHTML();
    } else {
        hiddenInput.value = quill.root.innerHTML;
    }
}

function dispatchHiddenInputEvents(hiddenInput) {
    hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
}

export function getRichTextEditor(rootEl) {
    if (!isRichTextEditorExternallyUpdatable(rootEl)) {
        return null;
    }

    return richTextEditorMap.get(rootEl) ?? null;
}

export function setRichTextEditorHtml(rootEl, html) {
    if (!rootEl || !isRichTextEditorExternallyUpdatable(rootEl)) {
        return false;
    }

    const normalizedHtml = typeof html === 'string' ? html : '';
    const hiddenInput = rootEl.querySelector('[data-rich-text-input]');
    const host = rootEl.querySelector('[data-rich-text-host]');
    const quill = getRichTextEditor(rootEl);

    if (quill) {
        if (normalizedHtml.trim() === '') {
            quill.setText('', 'silent');
        } else {
            const delta = quill.clipboard.convert({ html: normalizedHtml });
            quill.setContents(delta, 'silent');
        }

        if (hiddenInput) {
            syncToHidden(quill, hiddenInput);
            dispatchHiddenInputEvents(hiddenInput);
        }

        return true;
    }

    if (host) {
        const editor = host.querySelector('.ql-editor');
        if (editor) {
            editor.innerHTML = normalizedHtml;
        }
    }

    if (hiddenInput) {
        hiddenInput.value = normalizedHtml;
        dispatchHiddenInputEvents(hiddenInput);
    }

    return false;
}

/**
 * Snow テーマ向けの標準に近いツールバー（フォント・サイズ・見出し・配置など）
 * @see https://quilljs.com/docs/modules/toolbar
 */
function defaultToolbarOptions() {
    return [
        [{ font: [] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ header: 1 }, { header: 2 }, 'blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
    ];
}

export function initRichTextEditors(root = document) {
    const QuillCtor = window.Quill;
    if (!QuillCtor) {
        return;
    }

    root.querySelectorAll('[data-rich-text-editor]').forEach((rootEl) => {
        if (rootEl.getAttribute('data-rich-text-initialized') === '1') {
            return;
        }

        const host = rootEl.querySelector('[data-rich-text-host]');
        const hiddenInput = rootEl.querySelector('[data-rich-text-input]');
        if (!host || !hiddenInput) {
            return;
        }

        const placeholder = rootEl.dataset.placeholder || '';
        const height = rootEl.dataset.height || '220px';

        let quill;
        try {
            quill = new QuillCtor(host, {
                theme: 'snow',
                placeholder,
                modules: {
                    toolbar: defaultToolbarOptions(),
                },
            });
        } catch (err) {
            console.error('[rich-text-editor] Quill init failed', err);
            return;
        }

        const hostInner = host.querySelector('.ql-editor');
        if (hostInner) {
            hostInner.style.minHeight = height;
        }

        const initialHtml = getInitialHtml(rootEl);
        if (initialHtml.trim() !== '') {
            try {
                const delta = quill.clipboard.convert({ html: initialHtml });
                quill.setContents(delta, 'silent');
            } catch (err) {
                console.error('[rich-text-editor] Failed to apply initial HTML', err);
            }
        }

        const onChange = () => syncToHidden(quill, hiddenInput);
        quill.on('text-change', onChange);
        quill.on('selection-change', () => {
            if (quill.getSelection() === null) {
                onChange();
            }
        });
        onChange();

        if (isRichTextEditorExternallyUpdatable(rootEl)) {
            registerRichTextEditor(rootEl, quill);
        }
        rootEl.setAttribute('data-rich-text-initialized', '1');
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initRichTextEditors();
});
